import { useCallback, useMemo } from 'react';
import { useSetSelectedGrouping } from '@atlassian/jira-issue-table-group-by-dropdown/src/services/set-selected-grouping/useSetSelectedGrouping.tsx';
import { useIssueSearchActions } from '../issue-search/selectors.tsx';

import { getJqlFromMigrationData, getMigrationDataForProject } from './migration-data.tsx';

type UseListNINConfigMigrationProps = {
	projectId?: string | null;
	projectKey?: string | null;
	viewId: string;
	onChangeJqlAndGroupBy?: (params: { jql?: string; groupBy?: string }) => void;
};

const useListMigrationDataForProject = (projectId?: string | null) => {
	return useMemo(() => (projectId ? getMigrationDataForProject(projectId) : null), [projectId]);
};

/**
 * This hook is used to migrate filter and groupBy settings from the legacy List View stored in browser storage and apply them to NIN.
 * Remove when cleaning up jira_list_nin_merge_onboarding_modal
 * */
export const useListNINConfigMigration = ({
	projectId,
	projectKey,
	viewId,
	onChangeJqlAndGroupBy,
}: UseListNINConfigMigrationProps) => {
	const setSelectedGrouping = useSetSelectedGrouping();
	const { changeGroupedByFieldAndJql } = useIssueSearchActions();

	const storedMigrationData = useListMigrationDataForProject(projectId);

	const handleChangeGroupedByField = useCallback(
		(newGroupByField: { fieldId: string; name: string } | null) => {
			if (!newGroupByField?.fieldId || !storedMigrationData || !projectKey) {
				return;
			}
			const newJql = getJqlFromMigrationData(storedMigrationData, projectKey);

			changeGroupedByFieldAndJql(newGroupByField.fieldId, newJql);
			onChangeJqlAndGroupBy?.({
				jql: newJql,
				groupBy: newGroupByField.fieldId,
			});
		},
		[storedMigrationData, projectKey, changeGroupedByFieldAndJql, onChangeJqlAndGroupBy],
	);

	return useCallback(() => {
		if (!storedMigrationData || !projectKey) {
			return;
		}

		const { groupBy, filter, sorting } = storedMigrationData;

		if (groupBy) {
			setSelectedGrouping({
				fieldId: groupBy,
				fieldName: groupBy,
				viewId,
				onUpdateGroupByFieldAndRefetch: handleChangeGroupedByField,
			});
			return;
		}

		if (filter || sorting) {
			const newJql = getJqlFromMigrationData(storedMigrationData, projectKey);
			onChangeJqlAndGroupBy?.({ jql: newJql });
		}
	}, [
		viewId,
		storedMigrationData,
		setSelectedGrouping,
		handleChangeGroupedByField,
		onChangeJqlAndGroupBy,
		projectKey,
	]);
};
